import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import {
    Shield, LogOut, Users, Link2, ToggleLeft, ToggleRight,
    Activity, Gift, ChevronRight, Loader2, KeyRound, UserCog,
    Clock, MapPin, X, Lock, Crown, Pause, Play, RotateCcw, Plus,
    Zap, Ban, UserCheck, DollarSign, Check, ChevronDown, ChevronUp,
    Sparkles, Bookmark, BookmarkPlus, History, Trash2, Pencil,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import ManagerAccessRegistry from '../components/manager/ManagerAccessRegistry';
import NeoEmployeesPanel from '../components/neo/NeoEmployeesPanel';
import neoLandingBg from '../assets/neo-landing-bg.png';
import { SuccessCheck, PulseIcon, SkeletonList } from '../components/shared/Polish';
import { stringifyDetail } from '../lib/utils';
import { parseUtc } from '../lib/datetime';

const API_BASE = window.location.origin;
const INACTIVITY_SECONDS = 60;
const WARNING_SECONDS = 10;

const PLATFORMS = [
    { value: 'google_reviews', labelKey: 'manager.platformGoogle' },
    { value: 'facebook', labelKey: 'manager.platformFacebook' },
    { value: 'tripadvisor', labelKey: 'manager.platformTripadvisor' },
    { value: 'yelp', labelKey: 'manager.platformYelp' },
    { value: 'instagram', labelKey: 'manager.platformInstagram' },
];

// Reason codes — unified with Staff "Issue Voucher" UI (StaffMenu.jsx)
// Stripe color is auto-assigned, never manually selected
const REASON_CODES = [
    { code: 'late_food', labelKey: 'staffMenu.lateFoodReason', stripe: 'red' },
    { code: 'wrong_order', labelKey: 'staffMenu.wrongOrderReason', stripe: 'red' },
    { code: 'long_wait', labelKey: 'staffMenu.longWaitReason', stripe: 'red' },
    { code: 'manager_goodwill', labelKey: 'staffMenu.managerGoodwillReason', stripe: 'red' },
    { code: 'birthday', labelKey: 'staffMenu.birthdayReason', stripe: 'green' },
    { code: 'vip', labelKey: 'staffMenu.vipReason', stripe: 'gold' },
    { code: 'other', labelKey: 'staffMenu.otherReason', stripe: 'green' },
];

const STRIPE_STYLES = {
    red: { bg: 'bg-red-500/15 border-red-500/40 hover:bg-red-500/25', text: 'text-red-400', dot: 'bg-red-500' },
    green: { bg: 'bg-emerald-500/15 border-emerald-500/40 hover:bg-emerald-500/25', text: 'text-emerald-400', dot: 'bg-emerald-500' },
    gold: { bg: 'gold-gradient-bg-subtle gold-border gold-gradient-bg-hover', text: 'gold-gradient-text', dot: 'gold-gradient-dot' },
    blue: { bg: 'bg-blue-500/15 border-blue-500/40 hover:bg-blue-500/25', text: 'text-blue-400', dot: 'bg-blue-500' },
};

const DEFAULT_PRESET_AMOUNTS = [20, 25, 50, 75];
const DEFAULT_PRESET_EXPIRY = [30, 60, 90];
const MAX_TEMPLATES = 10;
const MAX_HISTORY = 5;

function _historyKey(cardId) { return `mgr_coupon_history_${cardId}`; }

function loadHistoryFromSession(cardId) {
    try {
        const raw = sessionStorage.getItem(_historyKey(cardId));
        if (!raw) return [];
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? arr.slice(0, MAX_HISTORY) : [];
    } catch { return []; }
}

function pushHistoryToSession(cardId, entry) {
    try {
        const curr = loadHistoryFromSession(cardId);
        const next = [entry, ...curr].slice(0, MAX_HISTORY);
        sessionStorage.setItem(_historyKey(cardId), JSON.stringify(next));
        return next;
    } catch { return []; }
}

export default function ManagerMode() {
    const { cardId } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [session, setSession] = useState(null); // {session_token, scope, expires_in}
    const [activity, setActivity] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [empDetail, setEmpDetail] = useState(null);
    const [accessOpen, setAccessOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showExitConfirm, setShowExitConfirm] = useState(false);
    const [cardState, setCardState] = useState({ platform: 'google_reviews', interaction_mode: 'standard', status: 'active' });
    const [draftCardState, setDraftCardState] = useState({ platform: 'google_reviews', interaction_mode: 'standard', status: 'active' });
    const [resetUrlPending, setResetUrlPending] = useState(false); // draft intent to reset URL
    const [savingCardSettings, setSavingCardSettings] = useState(false);
    const [exitGuardOpen, setExitGuardOpen] = useState(false);
    const pendingExitActionRef = useRef(null); // () => void | null
    const [vipState, setVipState] = useState({ vip_active: false, vip_expiry_time: null });
    const [vipModalOpen, setVipModalOpen] = useState(false);
    const [vipDuration, setVipDuration] = useState(30);
    const [vipCustom, setVipCustom] = useState(false);
    // VIP voucher fields (parity with Staff voucher→VIP flow)
    const [vipVoucherValue, setVipVoucherValue] = useState(null);
    const [vipVoucherCustomValue, setVipVoucherCustomValue] = useState('');
    const [vipVoucherShowCustom, setVipVoucherShowCustom] = useState(false);
    const [vipReason, setVipReason] = useState('vip');
    const [vipMessage, setVipMessage] = useState('');
    const [couponOpen, setCouponOpen] = useState(false);
    const [couponTab, setCouponTab] = useState('new'); // new | saved | history
    const [couponTemplates, setCouponTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [couponHistory, setCouponHistory] = useState([]);
    const [editingTemplateId, setEditingTemplateId] = useState(null); // null = creating new; else updating
    const [confirmDeleteTemplate, setConfirmDeleteTemplate] = useState(null); // {id, name}
    // Unified voucher/coupon builder state — mirrors Staff Issue Voucher UI
    const [orgSettings, setOrgSettings] = useState(null);
    const [couponReason, setCouponReason] = useState(null);
    const [couponValue, setCouponValue] = useState(null);
    const [couponCustomValue, setCouponCustomValue] = useState('');
    const [couponShowCustomValue, setCouponShowCustomValue] = useState(false);
    const [couponMessage, setCouponMessage] = useState('');
    const [couponAdvancedOpen, setCouponAdvancedOpen] = useState(false);
    const [couponCustomTitle, setCouponCustomTitle] = useState('');
    const [couponExpiryDate, setCouponExpiryDate] = useState('');
    const [couponQty, setCouponQty] = useState('');
    const [couponNotes, setCouponNotes] = useState('');
    const [savingCoupon, setSavingCoupon] = useState(false);
    const [savingTemplate, setSavingTemplate] = useState(false);

    // Inactivity tracking
    const [warningOpen, setWarningOpen] = useState(false);
    const [warningCountdown, setWarningCountdown] = useState(WARNING_SECONDS);
    const lastActivityRef = useRef(Date.now());
    const sessionStartRef = useRef(Date.now());
    const timerRef = useRef(null);

    // Hydrate session from sessionStorage (survives same-tab reload only)
    useEffect(() => {
        const raw = sessionStorage.getItem(`mgr_session_${cardId}`);
        if (raw) {
            try {
                const parsed = JSON.parse(raw);
                if (parsed.exp && parsed.exp > Date.now()) {
                    setSession(parsed);
                    sessionStartRef.current = parsed.startedAt || Date.now();
                }
            } catch {}
        }
    }, [cardId]);

    // NEO master switch — enforce at mount and whenever we navigate here.
    // If the Super Admin toggled NEO OFF on this location, we bounce the user
    // back to /landing and show the bilingual disabled toast. This closes the
    // direct-URL-navigation bypass (typing /manager/:id, stale tabs, etc.).
    useEffect(() => {
        let ignore = false;
        fetch(`${API_BASE}/api/neo/scan-route/${cardId}`)
            .then(r => r.ok ? r.json() : null)
            .then(d => {
                if (ignore || !d) return;
                if (!d.neo_enabled) {
                    sessionStorage.removeItem(`mgr_session_${cardId}`);
                    setSession(null);
                    toast(t('landing.portalDisabled', 'NEO is not enabled for this location. Please contact your administrator.'), {
                        id: 'neo-disabled',
                        duration: 3000,
                    });
                    navigate(`/landing/${cardId}`, { replace: true });
                }
            })
            .catch(() => {});
        return () => { ignore = true; };
    }, [cardId, navigate, t]);

    const token = session?.session_token;

    const authHeaders = () => ({ 'X-Manager-Session': token });

    const endSession = useCallback((reason, opts = {}) => {
        sessionStorage.removeItem(`mgr_session_${cardId}`);
        setSession(null);
        setWarningOpen(false);
        if (reason) toast.info(reason);
        if (opts.lock) {
            // Staff-parity: show the neutral "Session locked" toast + pass state so
            // LandingPage re-fires the same toast on the destination.
            toast(t('manager.sessionLocked', 'Session locked for your security'), {
                id: 'session-locked',
                duration: 2000,
            });
            navigate(`/landing/${cardId}`, { state: { sessionLocked: true } });
        } else {
            navigate(`/landing/${cardId}`);
        }
    }, [cardId, navigate, t]);

    // Record activity
    const bumpActivity = useCallback(() => {
        lastActivityRef.current = Date.now();
        if (warningOpen) setWarningOpen(false);
    }, [warningOpen]);

    // Inactivity + absolute session monitor
    useEffect(() => {
        if (!token) return;
        timerRef.current = setInterval(() => {
            const now = Date.now();
            const idle = (now - lastActivityRef.current) / 1000;
            const totalElapsed = (now - sessionStartRef.current) / 1000;

            // Absolute 5-minute kill
            if (totalElapsed >= 5 * 60) {
                endSession(null, { lock: true });
                return;
            }

            // 60s idle → open warning
            if (!warningOpen && idle >= INACTIVITY_SECONDS - WARNING_SECONDS) {
                setWarningOpen(true);
                setWarningCountdown(WARNING_SECONDS);
            }
            if (warningOpen) {
                const left = Math.max(0, INACTIVITY_SECONDS - Math.floor(idle));
                setWarningCountdown(left);
                if (left <= 0) endSession(null, { lock: true });
            }
        }, 1000);

        const events = ['mousedown', 'touchstart', 'keydown', 'scroll'];
        events.forEach(e => window.addEventListener(e, bumpActivity));

        // Visibility lock (parity with Staff Menu): backgrounding/app switch/screen lock
        // immediately ends the session with the same neutral lock toast.
        const handleVisibility = () => {
            if (document.visibilityState !== 'visible') {
                endSession(null, { lock: true });
            }
        };
        document.addEventListener('visibilitychange', handleVisibility);

        return () => {
            clearInterval(timerRef.current);
            events.forEach(e => window.removeEventListener(e, bumpActivity));
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, [token, warningOpen, bumpActivity, endSession, t]);

    // Browser-level unsaved-changes guard (refresh/close tab/navigate away)
    useEffect(() => {
        const hasDirty = draftCardState.platform !== cardState.platform ||
            draftCardState.interaction_mode !== cardState.interaction_mode ||
            draftCardState.status !== cardState.status ||
            resetUrlPending;
        if (!hasDirty) return;
        const onBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = '';
            return '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [draftCardState, cardState, resetUrlPending]);

    const loadAll = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const [aR, eR, cR, vR] = await Promise.all([
                fetch(`${API_BASE}/api/manager/activity?card_id=${cardId}`, { headers: authHeaders() }),
                fetch(`${API_BASE}/api/manager/employees?card_id=${cardId}`, { headers: authHeaders() }),
                fetch(`${API_BASE}/api/manager/card/${cardId}`, { headers: authHeaders() }),
                fetch(`${API_BASE}/api/manager/card/${cardId}/vip`, { headers: authHeaders() }),
            ]);
            if (aR.ok) setActivity(await aR.json());
            if (eR.ok) setEmployees(await eR.json());
            if (cR.ok) {
                const c = await cR.json();
                const fresh = {
                    platform: c.platform || 'google_reviews',
                    interaction_mode: c.interaction_mode || 'standard',
                    status: c.status || 'active',
                };
                setCardState(fresh);
                setDraftCardState(fresh);
                setResetUrlPending(false);
            }
            if (vR.ok) setVipState(await vR.json());
        } catch { /* silent */ }
        setLoading(false);
    }, [cardId, token]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { loadAll(); }, [loadAll]);

    // Fetch org voucher settings + templates + history when coupon modal opens
    useEffect(() => {
        if (!couponOpen) return;
        if (!orgSettings) {
            fetch(`${API_BASE}/api/voucher-settings/public/${cardId}`)
                .then(r => r.ok ? r.json() : null)
                .then(data => { if (data) setOrgSettings(data); })
                .catch(() => {});
        }
        // Load session history (no API — sessionStorage)
        setCouponHistory(loadHistoryFromSession(cardId));
        // Load templates
        if (!token) return;
        setTemplatesLoading(true);
        fetch(`${API_BASE}/api/manager/coupon-templates?card_id=${cardId}`, { headers: authHeaders() })
            .then(r => r.ok ? r.json() : [])
            .then(data => setCouponTemplates(Array.isArray(data) ? data : []))
            .catch(() => {})
            .finally(() => setTemplatesLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [couponOpen, cardId, token]);

    // ── DIRTY STATE DETECTION (card settings require explicit Save) ──
    const isCardSettingsDirty = () => (
        draftCardState.platform !== cardState.platform ||
        draftCardState.interaction_mode !== cardState.interaction_mode ||
        draftCardState.status !== cardState.status ||
        resetUrlPending
    );

    // Build pending-changes diff for the Recent Changes tooltip
    const computeCardSettingsDiff = () => {
        const changes = [];
        if (draftCardState.platform !== cardState.platform) {
            const fromLabel = t(PLATFORMS.find(p => p.value === cardState.platform)?.labelKey || '');
            const toLabel = t(PLATFORMS.find(p => p.value === draftCardState.platform)?.labelKey || '');
            changes.push({ key: 'platform', label: t('manager.changePlatform'), before: fromLabel, after: toLabel });
        }
        if (draftCardState.interaction_mode !== cardState.interaction_mode) {
            const before = cardState.interaction_mode === 'review_shield' ? t('manager.on') : t('manager.off');
            const after = draftCardState.interaction_mode === 'review_shield' ? t('manager.on') : t('manager.off');
            changes.push({ key: 'shield', label: t('manager.changeReviewShield'), before, after });
        }
        if (draftCardState.status !== cardState.status) {
            const before = cardState.status === 'paused' ? t('manager.pauseCard') : t('manager.resumeCard');
            const after = draftCardState.status === 'paused' ? t('manager.pauseCard') : t('manager.resumeCard');
            changes.push({ key: 'status', label: t('manager.changeStatus'), before, after });
        }
        if (resetUrlPending) {
            changes.push({ key: 'reset_url', label: t('manager.changeResetUrl'), before: '—', after: t('manager.changeResetUrlValue') });
        }
        return changes;
    };

    // Draft mutators — do NOT persist until Save is clicked
    const draftSetPlatform = (platform) => {
        bumpActivity();
        setDraftCardState(s => ({ ...s, platform }));
    };
    const draftToggleShield = () => {
        bumpActivity();
        setDraftCardState(s => ({
            ...s,
            interaction_mode: s.interaction_mode === 'review_shield' ? 'standard' : 'review_shield',
        }));
    };
    const draftTogglePause = () => {
        bumpActivity();
        setDraftCardState(s => ({
            ...s,
            status: s.status === 'paused' ? 'active' : 'paused',
        }));
    };
    const draftResetUrl = () => {
        bumpActivity();
        setResetUrlPending(v => !v);
    };

    const cancelCardSettings = () => {
        bumpActivity();
        setDraftCardState(cardState);
        setResetUrlPending(false);
    };

    const saveCardSettings = async () => {
        bumpActivity();
        if (!isCardSettingsDirty() || savingCardSettings) return;
        setSavingCardSettings(true);
        const calls = [];
        try {
            if (draftCardState.platform !== cardState.platform) {
                calls.push(fetch(`${API_BASE}/api/manager/card/${cardId}/platform`, {
                    method: 'PUT',
                    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                    body: JSON.stringify({ platform: draftCardState.platform }),
                }));
            }
            if (draftCardState.interaction_mode !== cardState.interaction_mode) {
                calls.push(fetch(`${API_BASE}/api/manager/card/${cardId}/review-shield`, {
                    method: 'PUT',
                    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                    body: JSON.stringify({ enabled: draftCardState.interaction_mode === 'review_shield' }),
                }));
            }
            if (draftCardState.status !== cardState.status) {
                const action = draftCardState.status === 'paused' ? 'pause' : 'resume';
                calls.push(fetch(`${API_BASE}/api/manager/card/${cardId}/${action}`, {
                    method: 'PUT', headers: authHeaders(),
                }));
            }
            if (resetUrlPending) {
                calls.push(fetch(`${API_BASE}/api/manager/card/${cardId}/reset-url`, {
                    method: 'PUT', headers: authHeaders(),
                }));
            }
            const results = await Promise.all(calls);
            const allOk = results.every(r => r.ok);
            if (allOk) {
                setCardState(draftCardState);
                setResetUrlPending(false);
                toast.success(t('common.saved'));
                loadAll();
            } else {
                toast.error(t('manager.failed'));
            }
        } catch { toast.error(t('manager.failed')); }
        finally { setSavingCardSettings(false); }
    };

    // Wrap any exit action with unsaved-changes guard
    const guardedExit = (action) => {
        if (isCardSettingsDirty()) {
            pendingExitActionRef.current = action;
            setExitGuardOpen(true);
        } else {
            action();
        }
    };

    const handleExitDiscard = () => {
        cancelCardSettings();
        setExitGuardOpen(false);
        const act = pendingExitActionRef.current;
        pendingExitActionRef.current = null;
        if (act) act();
    };
    const handleExitStay = () => {
        setExitGuardOpen(false);
        pendingExitActionRef.current = null;
    };
    const handleExitSave = async () => {
        await saveCardSettings();
        setExitGuardOpen(false);
        const act = pendingExitActionRef.current;
        pendingExitActionRef.current = null;
        if (act) setTimeout(act, 100);
    };

    const changePlatform = draftSetPlatform; // back-compat alias (Select onValueChange)
    const toggleShield = draftToggleShield;

    const openEmployee = async (empId) => {
        bumpActivity();
        try {
            const res = await fetch(`${API_BASE}/api/manager/employees/${empId}?card_id=${cardId}`, { headers: authHeaders() });
            if (res.ok) setEmpDetail(await res.json());
        } catch { /* silent */ }
    };

    // ── Phase 2 action handlers ──
    const doCardAction = async (endpoint, method = 'PUT') => {
        bumpActivity();
        try {
            const res = await fetch(`${API_BASE}/api/manager/card/${cardId}/${endpoint}`, {
                method, headers: authHeaders(),
            });
            if (res.ok) {
                toast.success(t('common.saved', 'Saved'));
                loadAll();
                return true;
            }
            toast.error(t('manager.failed'));
        } catch { toast.error(t('manager.failed')); }
        return false;
    };

    const activateVip = async () => {
        bumpActivity();
        const voucherVal = vipVoucherShowCustom ? parseFloat(vipVoucherCustomValue) : vipVoucherValue;
        if (!voucherVal || voucherVal <= 0) {
            toast.error(t('manager.couponValueInvalid'));
            return;
        }
        if (!vipReason) {
            toast.error(t('manager.couponSelectReason'));
            return;
        }
        try {
            const res = await fetch(`${API_BASE}/api/manager/card/${cardId}/vip/activate`, {
                method: 'POST',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    duration_minutes: Number(vipDuration),
                    voucher_value: voucherVal,
                    reason_code: vipReason,
                    message: vipMessage.trim() || null,
                }),
            });
            if (res.ok) {
                const data = await res.json();
                setVipState({ vip_active: true, vip_expiry_time: data.vip_expiry_time });
                toast.success(`${t('manager.vipActivated')} · ${data.voucher_code || ''}`);
                setVipModalOpen(false);
                // Reset VIP form for next time
                setVipVoucherValue(null);
                setVipVoucherCustomValue('');
                setVipVoucherShowCustom(false);
                setVipReason('vip');
                setVipMessage('');
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(stringifyDetail(err.detail) || t('manager.failed'));
            }
        } catch { toast.error(t('manager.failed')); }
    };

    const deactivateVip = async () => {
        bumpActivity();
        try {
            const res = await fetch(`${API_BASE}/api/manager/card/${cardId}/vip/deactivate`, {
                method: 'POST', headers: authHeaders(),
            });
            if (res.ok) {
                setVipState({ vip_active: false, vip_expiry_time: null });
                toast.success(t('manager.vipDeactivated'));
            } else toast.error(t('manager.failed'));
        } catch { toast.error(t('manager.failed')); }
    };

    const submitCoupon = async () => {
        bumpActivity();
        const effValue = couponShowCustomValue ? parseFloat(couponCustomValue) : couponValue;
        if (!couponReason) { toast.error(t('manager.couponSelectReason')); return; }
        if (!effValue || effValue <= 0) { toast.error(t('manager.couponValueInvalid')); return; }
        if (couponExpiryDate && parseUtc(couponExpiryDate) <= new Date()) {
            toast.error(t('manager.couponExpiryFuture')); return;
        }
        // Derive title: custom override takes precedence, else use reason label
        const reasonLabel = t(REASON_CODES.find(r => r.code === couponReason)?.labelKey || '');
        const derivedTitle = (couponCustomTitle.trim() || reasonLabel || '').trim();
        if (derivedTitle.length < 3) { toast.error(t('manager.couponTitleShort')); return; }

        setSavingCoupon(true);
        try {
            const res = await fetch(`${API_BASE}/api/manager/coupons?card_id=${cardId}`, {
                method: 'POST',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    title: derivedTitle,
                    value: effValue,
                    expiry_date: couponExpiryDate ? parseUtc(couponExpiryDate).toISOString() : null,
                    quantity_limit: couponQty ? Number(couponQty) : null,
                    notes: couponNotes.trim() || couponMessage.trim() || null,
                    reason_code: couponReason,
                    message: couponMessage.trim() || null,
                }),
            });
            if (res.ok) {
                const data = await res.json();
                // Push to session history (last 5)
                const historyEntry = {
                    reason_code: couponReason,
                    amount: effValue,
                    message: couponMessage.trim() || null,
                    expiry_date: couponExpiryDate || null,
                    title: couponCustomTitle.trim() || null,
                    notes: couponNotes.trim() || null,
                    code: data.code,
                    issued_at: new Date().toISOString(),
                    manager_name: session?.scope?.user_name || 'manager',
                };
                setCouponHistory(pushHistoryToSession(cardId, historyEntry));
                toast.success(`${t('manager.couponCreated')} · ${data.code}`);
                setCouponOpen(false);
                resetCouponForm();
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(stringifyDetail(err.detail) || t('manager.failed'));
            }
        } catch { toast.error(t('manager.failed')); }
        setSavingCoupon(false);
    };

    const resetCouponForm = () => {
        setCouponReason(null);
        setCouponValue(null);
        setCouponCustomValue('');
        setCouponShowCustomValue(false);
        setCouponMessage('');
        setCouponAdvancedOpen(false);
        setCouponCustomTitle('');
        setCouponExpiryDate('');
        setCouponQty('');
        setCouponNotes('');
        setEditingTemplateId(null);
        setCouponTab('new');
    };

    // ── Templates ──
    const getEffectiveAmount = () => couponShowCustomValue ? parseFloat(couponCustomValue) : couponValue;

    const buildTemplatePayload = () => {
        const effValue = getEffectiveAmount();
        if (!couponReason) { toast.error(t('manager.couponSelectReason')); return null; }
        if (!effValue || effValue <= 0) { toast.error(t('manager.couponValueInvalid')); return null; }
        const reasonLabel = t(REASON_CODES.find(r => r.code === couponReason)?.labelKey || '');
        const autoName = `${reasonLabel} — $${effValue}`;
        return {
            name: autoName,
            reason_code: couponReason,
            amount: effValue,
            message: couponMessage.trim() || null,
            expiry_date: couponExpiryDate ? parseUtc(couponExpiryDate).toISOString() : null,
            title: couponCustomTitle.trim() || null,
            notes: couponNotes.trim() || null,
        };
    };

    const saveAsTemplate = async () => {
        bumpActivity();
        const payload = buildTemplatePayload();
        if (!payload) return;
        if (!editingTemplateId && couponTemplates.length >= MAX_TEMPLATES) {
            toast.error(t('manager.templatesLimitReached', { max: MAX_TEMPLATES }));
            return;
        }
        setSavingTemplate(true);
        try {
            const url = editingTemplateId
                ? `${API_BASE}/api/manager/coupon-templates/${editingTemplateId}?card_id=${cardId}`
                : `${API_BASE}/api/manager/coupon-templates?card_id=${cardId}`;
            const method = editingTemplateId ? 'PUT' : 'POST';
            const res = await fetch(url, {
                method,
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (res.ok) {
                const data = await res.json();
                toast.success(t(editingTemplateId ? 'manager.templateUpdated' : 'manager.templateSaved'));
                setCouponTemplates(prev => {
                    if (editingTemplateId) return prev.map(tp => tp.id === editingTemplateId ? data : tp);
                    return [data, ...prev];
                });
                setEditingTemplateId(null);
                setCouponTab('saved');
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(stringifyDetail(err.detail) || t('manager.failed'));
            }
        } catch { toast.error(t('manager.failed')); }
        setSavingTemplate(false);
    };

    const applyTemplateToForm = (tpl) => {
        bumpActivity();
        setCouponReason(tpl.reason_code);
        // Map amount to preset or custom
        const presets = orgSettings?.preset_amounts || DEFAULT_PRESET_AMOUNTS;
        if (presets.includes(tpl.amount)) {
            setCouponValue(tpl.amount);
            setCouponShowCustomValue(false);
            setCouponCustomValue('');
        } else {
            setCouponShowCustomValue(true);
            setCouponCustomValue(String(tpl.amount));
            setCouponValue(null);
        }
        setCouponMessage(tpl.message || '');
        setCouponCustomTitle(tpl.title || '');
        setCouponNotes(tpl.notes || '');
        // Expiry_date: if ISO, extract yyyy-mm-dd; if past, skip
        if (tpl.expiry_date) {
            try {
                const d = parseUtc(tpl.expiry_date);
                if (d > new Date()) setCouponExpiryDate(d.toISOString().slice(0, 10));
                else setCouponExpiryDate('');
            } catch { setCouponExpiryDate(''); }
        } else {
            setCouponExpiryDate('');
        }
        setCouponAdvancedOpen(!!(tpl.title || tpl.notes || tpl.expiry_date));
        setEditingTemplateId(null);
        setCouponTab('new');
    };

    const editTemplate = (tpl) => {
        applyTemplateToForm(tpl);
        setEditingTemplateId(tpl.id);
    };

    const deleteTemplate = async (tplId) => {
        bumpActivity();
        try {
            const res = await fetch(`${API_BASE}/api/manager/coupon-templates/${tplId}?card_id=${cardId}`, {
                method: 'DELETE',
                headers: authHeaders(),
            });
            if (res.ok) {
                setCouponTemplates(prev => prev.filter(tp => tp.id !== tplId));
                toast.success(t('manager.templateDeleted'));
            } else toast.error(t('manager.failed'));
        } catch { toast.error(t('manager.failed')); }
        setConfirmDeleteTemplate(null);
    };

    // Format relative time for history (e.g. "2 min ago")
    const formatRelative = (iso) => {
        if (!iso) return '';
        const diffMs = Date.now() - parseUtc(iso).getTime();
        const minutes = Math.floor(diffMs / 60000);
        if (minutes < 1) return t('manager.historyRelativeJustNow');
        if (minutes < 60) return t('manager.historyRelativeMinutes', { count: minutes });
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return t('manager.historyRelativeHours', { count: hours });
        if (hours < 48) return t('manager.historyRelativeYesterday');
        return parseUtc(iso).toLocaleDateString();
    };

    const empAction = async (empId, action) => {
        bumpActivity();
        try {
            const res = await fetch(`${API_BASE}/api/manager/employees/${empId}/${action}?card_id=${cardId}`, {
                method: 'PUT', headers: authHeaders(),
            });
            if (res.ok) {
                toast.success(t('common.saved', 'Saved'));
                if (empDetail?.employee_id === empId) openEmployee(empId);
                const eR = await fetch(`${API_BASE}/api/manager/employees?card_id=${cardId}`, { headers: authHeaders() });
                if (eR.ok) setEmployees(await eR.json());
            } else toast.error(t('manager.failed'));
        } catch { toast.error(t('manager.failed')); }
    };

    // ── If no session, render PIN pad ──
    if (!token) {
        return <PinGate cardId={cardId} onSuccess={(sess) => {
            const entry = { ...sess, exp: Date.now() + (sess.expires_in - 2) * 1000, startedAt: Date.now() };
            sessionStorage.setItem(`mgr_session_${cardId}`, JSON.stringify(entry));
            sessionStartRef.current = Date.now();
            lastActivityRef.current = Date.now();
            setSession(entry);
        }} onBack={() => navigate(`/landing/${cardId}`)} />;
    }

    const scope = session.scope;
    const entryIsMaster = !!scope.entry_is_master;

    return (
        <div className="min-h-screen bg-slate-950 text-white p-4" data-testid="manager-mode-screen">
            {/* Header */}
            <div className="max-w-4xl mx-auto space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-900/60 border border-slate-800 rounded-xl p-3">
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-fuchsia-500 to-purple-600 flex items-center justify-center shrink-0">
                                <span className="text-white font-black text-[11px] tracking-tight">NEO</span>
                            </div>
                            <h1 className="text-base sm:text-lg font-bold truncate">
                                {scope.mode === 'elevated' ? t('manager.modeElevated') : t('manager.modeManager')}
                            </h1>
                            <span
                                className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border shrink-0 ${
                                    entryIsMaster
                                        ? 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40'
                                        : 'bg-slate-500/15 text-slate-300 border-slate-500/40'
                                }`}
                                data-testid="mgr-tier-badge"
                            >
                                {entryIsMaster
                                    ? t('manager.tierAdmin', 'Admin')
                                    : t('manager.tierOps', 'Ops')}
                            </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5 truncate">
                            {scope.user_name}{activity?.location_name ? ` · ${activity.location_name}` : ''}
                        </p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/landing/${cardId}`)}
                            className="text-slate-400 hover:text-white hover:bg-slate-800 min-h-[40px]"
                            data-testid="manager-back-btn"
                            aria-label={t('common.back', 'Back')}
                        >
                            <X className="w-4 h-4 mr-1" /> {t('common.back', 'Back')}
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => guardedExit(() => setShowExitConfirm(true))}
                            className="border-slate-700 text-slate-300 hover:bg-slate-800 min-h-[40px]"
                            data-testid="manager-exit-btn"
                        >
                            <LogOut className="w-4 h-4 mr-1" /> {t('manager.logoutBtn')}
                        </Button>
                    </div>
                </div>

                {/* VIP Mode Banner */}
                {vipState.vip_active && (
                    <div className="bg-gradient-to-r from-amber-500 to-yellow-500 text-black rounded-xl p-3 flex flex-wrap items-center justify-between gap-2 yp-elevate animate-in fade-in slide-in-from-top-2 duration-200" data-testid="vip-banner">
                        <div className="flex items-center gap-2 min-w-0">
                            <Crown className="w-5 h-5 shrink-0" />
                            <div className="min-w-0">
                                <p className="font-bold truncate">{t('manager.vipActive')}</p>
                                <p className="text-xs truncate">
                                    {t('manager.vipExpiresAt', { time: vipState.vip_expiry_time ? parseUtc(vipState.vip_expiry_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '' })}
                                </p>
                            </div>
                        </div>
                        <Button size="sm" variant="outline" onClick={deactivateVip} className="bg-black/10 border-black/30 text-black hover:bg-black/20 min-h-[40px] shrink-0" data-testid="vip-deactivate-btn">
                            <X className="w-3.5 h-3.5 mr-1" /> {t('manager.vipDeactivate')}
                        </Button>
                    </div>
                )}

                {/* Paused status banner */}
                {cardState.status === 'paused' && (
                    <div className="bg-orange-500/20 border border-orange-500/40 rounded-xl p-3 flex items-center gap-2 animate-in fade-in slide-in-from-top-2 duration-200" data-testid="paused-banner">
                        <Pause className="w-5 h-5 text-orange-400" />
                        <p className="text-sm font-medium text-orange-300">{t('manager.cardPausedBanner')}</p>
                    </div>
                )}

                {/* Live Activity */}
                <Section title={t('manager.liveActivity')} icon={<Activity className="w-4 h-4 text-emerald-400" />}>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <MetricCard label={t('manager.lastHour')} value={activity?.taps?.last_hour ?? 0} testid="mgr-taps-hour" />
                        <MetricCard label={t('manager.today')} value={activity?.taps?.today ?? 0} testid="mgr-taps-today" />
                        <MetricCard label={t('manager.thisWeek')} value={activity?.taps?.week ?? 0} testid="mgr-taps-week" />
                        <MetricCard label={t('manager.thisMonth')} value={activity?.taps?.month ?? 0} testid="mgr-taps-month" />
                    </div>
                </Section>

                {/* Vouchers */}
                <Section title={t('manager.vouchersTitle')} icon={<Gift className="w-4 h-4 text-amber-400" />}>
                    <div className="grid grid-cols-3 gap-2">
                        <MetricCard label={t('manager.createdToday')} value={activity?.vouchers?.created_today ?? 0} color="emerald" testid="mgr-v-created" />
                        <MetricCard label={t('manager.redeemedToday')} value={activity?.vouchers?.redeemed_today ?? 0} color="blue" testid="mgr-v-redeemed" />
                        <MetricCard label={t('manager.outstanding')} value={activity?.vouchers?.outstanding ?? 0} color="amber" testid="mgr-v-outstanding" />
                    </div>
                    <Button onClick={() => { bumpActivity(); setCouponOpen(true); }} className="w-full mt-2 min-h-[48px] bg-violet-600 hover:bg-violet-700" data-testid="mgr-create-coupon-btn">
                        <Plus className="w-4 h-4 mr-1" /> {t('manager.createCoupon')}
                    </Button>
                </Section>

                {/* Card Controls — available to any authenticated manager session
                    (relaxed from Master-card-only on 2026-02-19 so on-shift managers
                    can flip Review Shield / change platform without admin escort). */}
                <Section title={t('manager.cardControls')} icon={<Link2 className="w-4 h-4 text-blue-400" />}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-3 space-y-2">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                <Label className="text-xs text-slate-400 shrink-0">{t('manager.destinationUrl')}</Label>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={draftResetUrl}
                                    className={`h-8 px-2.5 text-[11px] shrink-0 ${resetUrlPending ? 'text-amber-400 hover:text-amber-300' : 'text-slate-400 hover:text-white'}`}
                                    data-testid="mgr-reset-url-btn"
                                >
                                    <RotateCcw className="w-3 h-3 mr-1 shrink-0" />
                                    <span>{resetUrlPending ? t('manager.resetPending') : t('manager.reset')}</span>
                                </Button>
                            </div>
                            <Select value={draftCardState.platform} onValueChange={draftSetPlatform}>
                                <SelectTrigger data-testid="mgr-platform-select" className="bg-slate-800 border-slate-700">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {PLATFORMS.map(p => (
                                        <SelectItem key={p.value} value={p.value}>{t(p.labelKey)}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-3 flex items-center justify-between">
                            <div>
                                <Label className="text-xs text-slate-400">{t('manager.reviewShield')}</Label>
                                <p className="text-sm font-semibold mt-1">
                                    {draftCardState.interaction_mode === 'review_shield' ? t('manager.on') : t('manager.off')}
                                </p>
                            </div>
                            <Button
                                onClick={draftToggleShield}
                                variant={draftCardState.interaction_mode === 'review_shield' ? 'default' : 'outline'}
                                className={draftCardState.interaction_mode === 'review_shield' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                                data-testid="mgr-shield-toggle"
                            >
                                {draftCardState.interaction_mode === 'review_shield' ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                            </Button>
                        </div>
                    </div>

                    {/* Phase 2: VIP + Pause/Resume */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mt-3">
                        {vipState.vip_active ? (
                            <Button onClick={deactivateVip} variant="outline" className="min-h-[44px] bg-amber-500/10 border-amber-500/40 text-amber-400 hover:bg-amber-500/20 justify-center" data-testid="mgr-vip-deactivate-inline">
                                <Crown className="w-4 h-4 mr-1 shrink-0" /> <span className="truncate">{t('manager.vipDeactivate')}</span>
                            </Button>
                        ) : (
                            <Button onClick={() => { bumpActivity(); setVipModalOpen(true); }} variant="outline" className="min-h-[44px] bg-amber-500/10 border-amber-500/40 text-amber-400 hover:bg-amber-500/20 justify-center" data-testid="mgr-vip-activate-btn">
                                <Crown className="w-4 h-4 mr-1 shrink-0" /> <span className="truncate">{t('manager.vipActivate')}</span>
                            </Button>
                        )}
                        {draftCardState.status === 'paused' ? (
                            <Button onClick={draftTogglePause} variant="outline" className="min-h-[44px] bg-emerald-500/10 border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/20 justify-center" data-testid="mgr-resume-card-btn">
                                <Play className="w-4 h-4 mr-1 shrink-0" /> <span className="truncate">{t('manager.resumeCard')}</span>
                            </Button>
                        ) : (
                            <Button onClick={draftTogglePause} variant="outline" className="min-h-[44px] bg-orange-500/10 border-orange-500/40 text-orange-400 hover:bg-orange-500/20 justify-center" data-testid="mgr-pause-card-btn">
                                <Pause className="w-4 h-4 mr-1 shrink-0" /> <span className="truncate">{t('manager.pauseCard')}</span>
                            </Button>
                        )}
                    </div>

                    {/* Save/Cancel footer — visible only when card settings are dirty */}
                    {isCardSettingsDirty() && (
                        <div
                            className="flex flex-col sm:flex-row gap-2 mt-4 pt-3 border-t border-slate-800 animate-in fade-in slide-in-from-bottom-2 duration-200"
                            data-testid="mgr-card-settings-footer"
                        >
                            <Popover>
                                <PopoverTrigger asChild>
                                    <button
                                        type="button"
                                        className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1 sm:mr-auto underline decoration-dotted underline-offset-4 transition-colors"
                                        data-testid="mgr-card-settings-dirty-indicator"
                                        aria-label={t('manager.viewChanges')}
                                    >
                                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                                        {t('manager.unsavedCardChanges')}
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent
                                    side="top"
                                    align="start"
                                    className="w-80 p-0 bg-slate-900 border-slate-700 text-slate-100"
                                    data-testid="mgr-changes-preview"
                                >
                                    <div className="px-4 py-3 border-b border-slate-800">
                                        <p className="text-sm font-semibold text-amber-400">{t('manager.changesPreviewTitle')}</p>
                                        <p className="text-[11px] text-slate-400 mt-0.5">{t('manager.changesPreviewHint')}</p>
                                    </div>
                                    <ul className="max-h-64 overflow-y-auto divide-y divide-slate-800" data-testid="mgr-changes-preview-list">
                                        {computeCardSettingsDiff().map(c => (
                                            <li key={c.key} className="px-4 py-2.5" data-testid={`mgr-change-${c.key}`}>
                                                <p className="text-[11px] uppercase tracking-wide text-slate-500">{c.label}</p>
                                                <div className="flex items-center gap-2 mt-1 text-sm">
                                                    <span className="line-through text-slate-500 truncate">{c.before}</span>
                                                    <span className="text-slate-500">→</span>
                                                    <span className="font-medium text-emerald-400 truncate">{c.after}</span>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </PopoverContent>
                            </Popover>
                            <Button
                                variant="outline"
                                onClick={cancelCardSettings}
                                disabled={savingCardSettings}
                                className="min-h-[44px] border-slate-700 text-slate-300 hover:bg-slate-800"
                                data-testid="mgr-card-settings-cancel"
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button
                                onClick={saveCardSettings}
                                disabled={savingCardSettings}
                                className="min-h-[44px] bg-emerald-600 hover:bg-emerald-700"
                                data-testid="mgr-card-settings-save"
                            >
                                {savingCardSettings ? (
                                    <><Loader2 className="w-4 h-4 animate-spin mr-1" /> {t('common.saving')}</>
                                ) : (
                                    <><Check className="w-4 h-4 mr-1" /> {t('common.saveChanges')}</>
                                )}
                            </Button>
                        </div>
                    )}
                </Section>

                {/* Tap’N Win — only when the Super Admin switch is ON for this
                    organization. NEO reads that switch, it never changes it. */}
                {scope.goal_game_enabled && (
                    <Section title={t('neoGame.sectionTitle', 'Tap’N Win')}
                             icon={<Gift className="w-4 h-4 text-emerald-400" />}>
                        <p className="text-xs text-slate-400 mb-2">
                            {t('neoGame.hint', 'Your own players, for this venue.')}
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <Button
                                onClick={() => { bumpActivity(); navigate(`/manager/${cardId}/live-game-view`); }}
                                className="w-full min-h-[48px] bg-emerald-600 hover:bg-emerald-700"
                                data-testid="mgr-live-game-btn"
                            >
                                <Activity className="w-4 h-4 mr-1.5" />
                                {t('neoGame.openLive', 'Live Game View')}
                            </Button>
                            <Button
                                onClick={() => { bumpActivity(); navigate(`/manager/${cardId}/game-results`); }}
                                variant="outline"
                                className="w-full min-h-[48px] border-slate-700 text-slate-200 hover:bg-slate-800"
                                data-testid="mgr-game-results-btn"
                            >
                                <Activity className="w-4 h-4 mr-1.5" />
                                {t('neoGame.openResults', 'Game Results')}
                            </Button>
                        </div>
                    </Section>
                )}

                {/* Manage Access (elevated + Master-card entry only) */}
                {scope.can_manage_access && entryIsMaster && (
                    <Section title={t('manager.manageAccess')} icon={<UserCog className="w-4 h-4 text-fuchsia-400" />}>
                        <Button
                            onClick={() => { bumpActivity(); setAccessOpen(true); }}
                            className="w-full bg-fuchsia-600 hover:bg-fuchsia-700"
                            data-testid="mgr-manage-access-btn"
                        >
                            <KeyRound className="w-4 h-4 mr-1.5" /> {t('manager.manageAccess')}
                        </Button>
                    </Section>
                )}

                {/* NEO — Employees grid + bulk actions + media quick-access (Manager-tier only) */}
                {entryIsMaster && (
                <Section title="NEO" icon={<div className="w-5 h-5 rounded-md bg-gradient-to-br from-fuchsia-500 to-purple-600 flex items-center justify-center"><span className="text-white font-black text-[9px]">NEO</span></div>}>
                    <NeoEmployeesPanel
                        cardId={cardId}
                        sessionToken={token}
                        employees={employees}
                        onRefresh={async () => {
                            try {
                                const eR = await fetch(`${API_BASE}/api/manager/employees?card_id=${cardId}`, { headers: authHeaders() });
                                if (eR.ok) setEmployees(await eR.json());
                            } catch {}
                        }}
                    />
                </Section>
                )}

                {/* Employees */}
                <Section title={t('manager.employeesTitle')} icon={<Users className="w-4 h-4 text-violet-400" />}>
                    {loading ? (
                        <SkeletonList rows={4} className="px-2" />
                    ) : employees.length === 0 ? (
                        <p className="text-center text-slate-500 text-sm py-6">{t('manager.noEmployees')}</p>
                    ) : (
                        <div className="divide-y divide-slate-800 rounded-lg border border-slate-800 overflow-hidden">
                            {employees.map(e => (
                                <button
                                    key={e.employee_id}
                                    onClick={() => openEmployee(e.employee_id)}
                                    className={`w-full flex items-center justify-between p-3 hover:bg-slate-900/40 transition-all duration-150 ease-out active:scale-[0.98] text-left ${e.is_active === false ? 'opacity-50' : ''}`}
                                    data-testid={`mgr-emp-${e.employee_id}`}
                                >
                                    <div>
                                        <p className="font-medium text-white flex items-center gap-1.5">
                                            {e.name}
                                            {e.is_active === false && <span className="text-[10px] bg-red-500/20 text-red-300 px-1.5 py-0.5 rounded-full">{t('manager.statusInactive')}</span>}
                                        </p>
                                        <p className="text-xs text-slate-500">{e.position}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-xs text-slate-400">{t('manager.tapsToday')}: <span className="text-emerald-400 font-semibold">{e.taps_today}</span></p>
                                        <p className="text-xs text-slate-400">{t('manager.incentiveBalance')}: <span className="text-blue-400 font-semibold">${(e.incentive_balance || 0).toFixed(2)}</span></p>
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-slate-600 ml-2" />
                                </button>
                            ))}
                        </div>
                    )}
                </Section>
            </div>

            {/* Exit Confirmation */}
            <Dialog open={showExitConfirm} onOpenChange={setShowExitConfirm}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('manager.logoutBtn')}</DialogTitle>
                        <DialogDescription>{t('manager.logoutConfirm')}</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowExitConfirm(false)}>{t('manager.cancel')}</Button>
                        <Button variant="destructive" onClick={() => endSession()} data-testid="mgr-exit-confirm-btn" data-insight-id="neo.workflow.end-session" data-insight-label="End NEO manager session">
                            <LogOut className="w-4 h-4 mr-1" /> {t('manager.logoutBtn')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Unsaved-Changes Exit Guard */}
            <Dialog open={exitGuardOpen} onOpenChange={(o) => { if (!o) handleExitStay(); }}>
                <DialogContent data-testid="mgr-unsaved-guard">
                    <DialogHeader>
                        <DialogTitle>{t('common.unsavedChanges')}</DialogTitle>
                        <DialogDescription>{t('common.unsavedChangesDesc')}</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex-col sm:flex-row gap-2">
                        <Button variant="outline" onClick={handleExitDiscard} className="min-h-[44px]" data-testid="mgr-unsaved-discard">
                            {t('common.discard')}
                        </Button>
                        <Button variant="secondary" onClick={handleExitStay} className="min-h-[44px]" data-testid="mgr-unsaved-stay">
                            {t('common.stay')}
                        </Button>
                        <Button onClick={handleExitSave} disabled={savingCardSettings} className="min-h-[44px] bg-emerald-600 hover:bg-emerald-700" data-testid="mgr-unsaved-save">
                            {savingCardSettings
                                ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> {t('common.saving')}</>
                                : <><Check className="w-4 h-4 mr-1" /> {t('common.save')}</>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Inactivity Warning */}
            <Dialog open={warningOpen} onOpenChange={setWarningOpen}>
                <DialogContent data-testid="mgr-inactivity-warning">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><Clock className="w-5 h-5 text-amber-500" /> {t('manager.warningTimeout', { seconds: warningCountdown })}</DialogTitle>
                    </DialogHeader>
                    <DialogFooter>
                        <Button onClick={bumpActivity} className="bg-emerald-600 hover:bg-emerald-700" data-testid="mgr-stay-active-btn">
                            {t('manager.stayActive')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Employee Detail Drawer */}
            <Dialog open={!!empDetail} onOpenChange={(o) => { if (!o) setEmpDetail(null); }}>
                <DialogContent data-testid="mgr-emp-detail">
                    <DialogHeader>
                        <DialogTitle>{empDetail?.name}</DialogTitle>
                        <DialogDescription>{empDetail?.position}</DialogDescription>
                    </DialogHeader>
                    {empDetail && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-2">
                                <MetricCard label={t('manager.lastHour')} value={empDetail.taps.last_hour} />
                                <MetricCard label={t('manager.today')} value={empDetail.taps.today} />
                                <MetricCard label={t('manager.thisWeek')} value={empDetail.taps.week} />
                                <MetricCard label={t('manager.thisMonth')} value={empDetail.taps.month} />
                            </div>
                            <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-3 space-y-1">
                                <p className="text-xs text-slate-400">{t('manager.incentiveBalance')}</p>
                                <p className="text-2xl font-bold text-blue-400">${(empDetail.incentive_balance || 0).toFixed(2)}</p>
                                <p className="text-xs text-slate-400 mt-1">
                                    {t('manager.lastPayout')}: {empDetail.last_payout_date
                                        ? `$${(empDetail.last_payout_amount || 0).toFixed(2)} — ${parseUtc(empDetail.last_payout_date).toLocaleDateString()}`
                                        : t('manager.never')}
                                </p>
                            </div>
                            {/* Phase 2: Employee extended controls */}
                            <div className="grid grid-cols-1 gap-2 pt-1 border-t border-slate-800">
                                <Button
                                    variant="outline"
                                    onClick={() => empAction(empDetail.employee_id, 'reset-balance')}
                                    disabled={!(empDetail.incentive_balance > 0)}
                                    className="bg-blue-500/10 border-blue-500/40 text-blue-400 hover:bg-blue-500/20"
                                    data-testid="mgr-emp-reset-balance-btn"
                                >
                                    <DollarSign className="w-4 h-4 mr-1" /> {t('manager.resetBalance')}
                                </Button>
                                {empDetail.is_active !== false ? (
                                    <Button
                                        variant="outline"
                                        onClick={() => empAction(empDetail.employee_id, 'deactivate')}
                                        className="bg-red-500/10 border-red-500/40 text-red-400 hover:bg-red-500/20"
                                        data-testid="mgr-emp-deactivate-btn"
                                    >
                                        <Ban className="w-4 h-4 mr-1" /> {t('manager.deactivate')}
                                    </Button>
                                ) : (
                                    <Button
                                        variant="outline"
                                        onClick={() => empAction(empDetail.employee_id, 'reactivate')}
                                        className="bg-emerald-500/10 border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/20"
                                        data-testid="mgr-emp-reactivate-btn"
                                    >
                                        <UserCheck className="w-4 h-4 mr-1" /> {t('manager.reactivate')}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* VIP Activate Modal */}
            <Dialog open={vipModalOpen} onOpenChange={setVipModalOpen}>
                <DialogContent data-testid="vip-activate-modal" className="max-w-md max-h-[92vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><Crown className="w-5 h-5 text-amber-500" /> {t('manager.vipActivate')}</DialogTitle>
                        <DialogDescription>{t('manager.vipActivateHint')}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        {/* Voucher value (NEW — VIP now loads a real gift card) */}
                        <div className="space-y-2">
                            <label className="text-sm text-muted-foreground flex items-center gap-1">
                                <DollarSign className="w-3.5 h-3.5" /> {t('manager.couponAmount')}
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                                {(orgSettings?.preset_amounts || DEFAULT_PRESET_AMOUNTS).map(v => (
                                    <button
                                        key={v}
                                        type="button"
                                        data-testid={`vip-value-${v}`}
                                        onClick={() => { setVipVoucherValue(v); setVipVoucherShowCustom(false); setVipVoucherCustomValue(''); }}
                                        className={`h-14 rounded-lg font-bold text-lg transition-all duration-150 active:scale-[0.98] ${
                                            vipVoucherValue === v && !vipVoucherShowCustom
                                                ? 'bg-amber-500 text-black ring-2 ring-amber-300'
                                                : 'bg-slate-800 text-slate-200 hover:bg-slate-700'
                                        }`}
                                    >${v}</button>
                                ))}
                            </div>
                            {!vipVoucherShowCustom ? (
                                <button
                                    type="button"
                                    onClick={() => { setVipVoucherShowCustom(true); setVipVoucherValue(null); }}
                                    className="text-xs text-amber-400 hover:text-amber-300"
                                    data-testid="vip-custom-amount-toggle"
                                >
                                    {t('manager.couponCustomAmount')}
                                </button>
                            ) : (
                                <Input
                                    data-testid="vip-custom-amount-input"
                                    type="number" step="0.01" min={0.01} max={10000}
                                    value={vipVoucherCustomValue}
                                    onChange={(e) => setVipVoucherCustomValue(e.target.value)}
                                    placeholder={t('manager.couponAmount')}
                                    className="bg-slate-900 border-slate-700 text-sm h-11"
                                    autoFocus
                                />
                            )}
                        </div>

                        {/* Reason */}
                        <div className="space-y-2">
                            <label className="text-sm text-muted-foreground flex items-center gap-1">
                                <Sparkles className="w-3.5 h-3.5" /> {t('manager.couponReason')}
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                                {REASON_CODES.map(r => {
                                    const style = STRIPE_STYLES[r.stripe] || STRIPE_STYLES.green;
                                    const isSelected = vipReason === r.code;
                                    return (
                                        <button
                                            key={r.code}
                                            type="button"
                                            data-testid={`vip-reason-${r.code}`}
                                            onClick={() => setVipReason(r.code)}
                                            className={`min-h-[44px] rounded-lg text-xs font-medium px-2 py-2 transition-all duration-150 active:scale-[0.98] border flex items-center gap-1.5 ${
                                                isSelected
                                                    ? `${style.bg} ${style.text} border-current ring-1 ring-current/30`
                                                    : 'bg-slate-800 text-slate-200 hover:bg-slate-700 border-slate-700'
                                            }`}
                                        >
                                            <span className={`w-2 h-2 rounded-full shrink-0 ${style.dot}`} />
                                            <span className="truncate">{t(r.labelKey)}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Optional message */}
                        <div className="space-y-1">
                            <label className="text-xs text-muted-foreground">{t('manager.couponMessage')}</label>
                            <Input
                                data-testid="vip-message-input"
                                value={vipMessage}
                                onChange={(e) => setVipMessage(e.target.value)}
                                placeholder={t('manager.couponMessagePlaceholder')}
                                className="bg-slate-900 border-slate-700 text-sm h-11"
                                maxLength={200}
                            />
                        </div>

                        {/* Duration (how long the VIP button stays active) */}
                        <div className="space-y-2 border-t border-slate-800 pt-3">
                            <label className="text-sm text-muted-foreground flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5" /> {t('manager.vipDuration', 'VIP button active for')}
                            </label>
                            {!vipCustom && (
                                <div className="grid grid-cols-2 gap-2">
                                    {[15, 30, 60, 120].map(m => (
                                        <Button
                                            key={m}
                                            variant={vipDuration === m ? 'default' : 'outline'}
                                            onClick={() => setVipDuration(m)}
                                            className={vipDuration === m ? 'bg-amber-500 hover:bg-amber-600 text-black min-h-[44px]' : 'min-h-[44px]'}
                                            data-testid={`vip-duration-${m}`}
                                        >
                                            <Zap className="w-3.5 h-3.5 mr-1" /> {m < 60 ? t('manager.nMinutes', { count: m }) : t('manager.nHours', { count: m / 60 })}
                                        </Button>
                                    ))}
                                </div>
                            )}
                            <button onClick={() => setVipCustom(v => !v)} className="text-xs text-muted-foreground underline" data-testid="vip-custom-toggle">
                                {vipCustom ? t('manager.useSelect') : t('manager.useCustom')}
                            </button>
                            {vipCustom && (
                                <div className="space-y-1">
                                    <Label className="text-xs">{t('manager.customDurationMinutes')}</Label>
                                    <Input
                                        type="number" min={1} max={1440}
                                        value={vipDuration}
                                        onChange={(e) => setVipDuration(e.target.value)}
                                        data-testid="vip-custom-input"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
                        <Button variant="outline" onClick={() => setVipModalOpen(false)} className="min-h-[44px]">{t('manager.cancel')}</Button>
                        <Button
                            onClick={activateVip}
                            className="bg-amber-500 hover:bg-amber-600 text-black min-h-[44px]"
                            disabled={!vipReason || !((vipVoucherShowCustom ? parseFloat(vipVoucherCustomValue) > 0 : vipVoucherValue > 0))}
                            data-testid="vip-activate-confirm"
                        >
                            <Crown className="w-4 h-4 mr-1" /> {t('manager.vipActivate')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Coupon Create Modal — Tabs: New · Saved · History */}
            <Dialog open={couponOpen} onOpenChange={(o) => { setCouponOpen(o); if (!o) resetCouponForm(); }}>
                <DialogContent data-testid="coupon-create-modal" className="max-w-md max-h-[92vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Gift className="w-5 h-5 text-violet-500" /> {t('manager.couponBuilderTitle')}
                        </DialogTitle>
                        <DialogDescription>{t('manager.couponBuilderSubtitle')}</DialogDescription>
                    </DialogHeader>

                    <Tabs value={couponTab} onValueChange={(v) => { bumpActivity(); setCouponTab(v); }} className="w-full">
                        <TabsList className="grid grid-cols-3 w-full" data-testid="coupon-tabs">
                            <TabsTrigger value="new" data-testid="coupon-tab-new" className="min-h-[40px]">
                                <Plus className="w-3.5 h-3.5 mr-1" /> {t('manager.tabNew')}
                            </TabsTrigger>
                            <TabsTrigger value="saved" data-testid="coupon-tab-saved" className="min-h-[40px]">
                                <Bookmark className="w-3.5 h-3.5 mr-1" /> {t('manager.tabSaved')}
                                {couponTemplates.length > 0 && (
                                    <span className="ml-1 text-[10px] bg-violet-500/20 text-violet-300 px-1.5 rounded-full">{couponTemplates.length}</span>
                                )}
                            </TabsTrigger>
                            <TabsTrigger value="history" data-testid="coupon-tab-history" className="min-h-[40px]">
                                <History className="w-3.5 h-3.5 mr-1" /> {t('manager.tabHistory')}
                                {couponHistory.length > 0 && (
                                    <span className="ml-1 text-[10px] bg-violet-500/20 text-violet-300 px-1.5 rounded-full">{couponHistory.length}</span>
                                )}
                            </TabsTrigger>
                        </TabsList>

                        {/* NEW TAB — builder form (unchanged) */}
                        <TabsContent value="new" className="space-y-5 pt-4 mt-0" data-testid="coupon-new-panel">
                            {/* Amount */}
                            <div className="space-y-2">
                                <label className="text-sm text-muted-foreground flex items-center gap-1">
                                    <DollarSign className="w-3.5 h-3.5" /> {t('manager.couponAmount')}
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    {(orgSettings?.preset_amounts || DEFAULT_PRESET_AMOUNTS).map(v => (
                                        <button
                                            key={v}
                                            type="button"
                                            data-testid={`coupon-value-${v}`}
                                            onClick={() => { setCouponValue(v); setCouponShowCustomValue(false); setCouponCustomValue(''); }}
                                            className={`h-14 rounded-lg font-bold text-lg transition-all duration-150 active:scale-[0.98] ${
                                                couponValue === v && !couponShowCustomValue
                                                    ? 'bg-emerald-600 text-white ring-2 ring-emerald-400'
                                                    : 'bg-slate-800 text-slate-200 hover:bg-slate-700'
                                            }`}
                                        >${v}</button>
                                    ))}
                                </div>
                                {!couponShowCustomValue ? (
                                    <button
                                        type="button"
                                        onClick={() => { setCouponShowCustomValue(true); setCouponValue(null); }}
                                        className="text-xs text-emerald-400 hover:text-emerald-300"
                                        data-testid="coupon-custom-amount-toggle"
                                    >
                                        {t('manager.couponCustomAmount')}
                                    </button>
                                ) : (
                                    <div className="flex gap-2 mt-1 items-center">
                                        <Input
                                            data-testid="coupon-custom-amount-input"
                                            type="number"
                                            step="0.01"
                                            min={0.01}
                                            max={10000}
                                            value={couponCustomValue}
                                            onChange={(e) => setCouponCustomValue(e.target.value)}
                                            placeholder={t('manager.couponAmount')}
                                            className="bg-slate-900 border-slate-700 text-sm flex-1 h-11"
                                            autoFocus
                                        />
                                        <button
                                            type="button"
                                            onClick={() => { setCouponShowCustomValue(false); setCouponCustomValue(''); }}
                                            className="text-xs text-muted-foreground px-2"
                                        >
                                            {t('manager.cancel')}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Reason */}
                            <div className="space-y-2">
                                <label className="text-sm text-muted-foreground flex items-center gap-1">
                                    <Sparkles className="w-3.5 h-3.5" /> {t('manager.couponReason')}
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    {REASON_CODES.map(r => {
                                        const style = STRIPE_STYLES[r.stripe] || STRIPE_STYLES.green;
                                        const isSelected = couponReason === r.code;
                                        return (
                                            <button
                                                key={r.code}
                                                type="button"
                                                data-testid={`coupon-reason-${r.code}`}
                                                onClick={() => setCouponReason(r.code)}
                                                className={`min-h-[44px] rounded-lg text-xs font-medium px-2 py-2 transition-all duration-150 active:scale-[0.98] border flex items-center gap-1.5 ${
                                                    isSelected
                                                        ? `${style.bg} ${style.text} border-current ring-1 ring-current/30`
                                                        : 'bg-slate-800 text-slate-200 hover:bg-slate-700 border-slate-700'
                                                }`}
                                            >
                                                <span className={`w-2 h-2 rounded-full shrink-0 ${style.dot}`} />
                                                <span className="truncate">{t(r.labelKey)}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Message */}
                            <div className="space-y-2">
                                <label className="text-sm text-muted-foreground">{t('manager.couponMessage')}</label>
                                <Input
                                    data-testid="coupon-message-input"
                                    value={couponMessage}
                                    onChange={(e) => setCouponMessage(e.target.value)}
                                    placeholder={t('manager.couponMessagePlaceholder')}
                                    className="bg-slate-900 border-slate-700 text-sm h-11"
                                    maxLength={200}
                                />
                            </div>

                            {/* Advanced Options (collapsed by default — Manager-only overrides) */}
                            <div className="border-t border-slate-800 pt-3">
                                <button
                                    type="button"
                                    onClick={() => setCouponAdvancedOpen(v => !v)}
                                    className="w-full flex items-center justify-between text-left px-2 py-2 rounded-lg hover:bg-slate-800/40 transition-colors"
                                    data-testid="coupon-advanced-toggle"
                                    aria-expanded={couponAdvancedOpen}
                                >
                                    <div>
                                        <p className="text-sm font-semibold text-slate-200">{t('manager.advancedOptions')}</p>
                                        <p className="text-[11px] text-muted-foreground mt-0.5">{t('manager.advancedOptionsHint')}</p>
                                    </div>
                                    {couponAdvancedOpen
                                        ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" />
                                        : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />}
                                </button>

                                {couponAdvancedOpen && (
                                    <div className="space-y-3 pt-3 animate-in fade-in slide-in-from-top-2 duration-200" data-testid="coupon-advanced-panel">
                                        <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground">{t('manager.couponCustomTitle')}</Label>
                                            <Input
                                                value={couponCustomTitle}
                                                onChange={(e) => setCouponCustomTitle(e.target.value)}
                                                placeholder={t('manager.couponCustomTitlePlaceholder')}
                                                maxLength={120}
                                                className="h-11 bg-slate-900 border-slate-700"
                                                data-testid="coupon-title-input"
                                            />
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            <div className="space-y-1">
                                                <Label className="text-xs text-muted-foreground">{t('manager.couponExpiry')}</Label>
                                                <Input
                                                    type="date"
                                                    value={couponExpiryDate}
                                                    onChange={(e) => setCouponExpiryDate(e.target.value)}
                                                    className="h-11 bg-slate-900 border-slate-700"
                                                    data-testid="coupon-expiry-input"
                                                />
                                            </div>
                                            <div className="space-y-1">
                                                <Label className="text-xs text-muted-foreground">{t('manager.couponQty')}</Label>
                                                <Input
                                                    type="number"
                                                    min={1}
                                                    max={10000}
                                                    value={couponQty}
                                                    onChange={(e) => setCouponQty(e.target.value)}
                                                    className="h-11 bg-slate-900 border-slate-700"
                                                    data-testid="coupon-qty-input"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground">{t('manager.couponNotes')}</Label>
                                            <Input
                                                value={couponNotes}
                                                onChange={(e) => setCouponNotes(e.target.value)}
                                                maxLength={500}
                                                className="h-11 bg-slate-900 border-slate-700"
                                                data-testid="coupon-notes-input"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </TabsContent>

                        {/* SAVED TAB — manager-defined templates */}
                        <TabsContent value="saved" className="space-y-3 pt-4 mt-0" data-testid="coupon-saved-panel">
                            {templatesLoading ? (
                                <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-violet-400" /></div>
                            ) : couponTemplates.length === 0 ? (
                                <div className="text-center py-10 px-4">
                                    <Bookmark className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                                    <p className="text-sm text-muted-foreground">{t('manager.templatesEmpty')}</p>
                                </div>
                            ) : (
                                <div className="space-y-2" data-testid="coupon-templates-list">
                                    {couponTemplates.map(tpl => {
                                        const rObj = REASON_CODES.find(r => r.code === tpl.reason_code);
                                        const style = STRIPE_STYLES[rObj?.stripe] || STRIPE_STYLES.green;
                                        return (
                                            <div
                                                key={tpl.id}
                                                className="group rounded-lg border border-slate-800 bg-slate-900/40 hover:bg-slate-900/70 transition-colors overflow-hidden"
                                                data-testid={`coupon-template-${tpl.id}`}
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() => applyTemplateToForm(tpl)}
                                                    className="w-full flex items-center gap-3 px-3 py-3 text-left active:scale-[0.99] transition-transform min-h-[60px]"
                                                    data-testid={`coupon-template-apply-${tpl.id}`}
                                                >
                                                    <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${style.dot}`} />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-semibold text-slate-100 truncate">{tpl.name}</p>
                                                        <p className="text-[11px] text-muted-foreground truncate">
                                                            {t(rObj?.labelKey || '')} · ${tpl.amount}
                                                        </p>
                                                    </div>
                                                    <ChevronRight className="w-4 h-4 text-slate-600 shrink-0" />
                                                </button>
                                                <div className="flex items-center justify-end gap-1 px-2 pb-2 border-t border-slate-800/60">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => editTemplate(tpl)}
                                                        className="h-8 px-3 text-xs text-slate-400 hover:text-white"
                                                        data-testid={`coupon-template-edit-${tpl.id}`}
                                                    >
                                                        <Pencil className="w-3 h-3 mr-1 shrink-0" /> {t('manager.templateEditBtn')}
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => setConfirmDeleteTemplate({ id: tpl.id, name: tpl.name })}
                                                        className="h-8 px-3 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                                        data-testid={`coupon-template-delete-${tpl.id}`}
                                                    >
                                                        <Trash2 className="w-3 h-3 mr-1 shrink-0" /> {t('manager.templateDeleteBtn')}
                                                    </Button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </TabsContent>

                        {/* HISTORY TAB — sessionStorage, last 5 issued */}
                        <TabsContent value="history" className="space-y-2 pt-4 mt-0" data-testid="coupon-history-panel">
                            {couponHistory.length === 0 ? (
                                <div className="text-center py-10 px-4">
                                    <History className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                                    <p className="text-sm text-muted-foreground">{t('manager.historyEmpty')}</p>
                                </div>
                            ) : (
                                <div className="space-y-1.5" data-testid="coupon-history-list">
                                    {couponHistory.map((h, idx) => {
                                        const rObj = REASON_CODES.find(r => r.code === h.reason_code);
                                        const style = STRIPE_STYLES[rObj?.stripe] || STRIPE_STYLES.green;
                                        return (
                                            <button
                                                key={`${h.code}-${idx}`}
                                                type="button"
                                                onClick={() => applyTemplateToForm({
                                                    reason_code: h.reason_code,
                                                    amount: h.amount,
                                                    message: h.message,
                                                    expiry_date: h.expiry_date,
                                                    title: h.title,
                                                    notes: h.notes,
                                                })}
                                                className="w-full flex items-center gap-3 px-3 py-3 text-left rounded-lg border border-slate-800 bg-slate-900/40 hover:bg-slate-900/70 transition-colors active:scale-[0.99] min-h-[60px]"
                                                data-testid={`coupon-history-item-${idx}`}
                                            >
                                                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${style.dot}`} />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-semibold text-slate-100 truncate">
                                                        {t(rObj?.labelKey || '')} · ${h.amount}
                                                    </p>
                                                    <p className="text-[11px] text-muted-foreground truncate">
                                                        {formatRelative(h.issued_at)} · {h.manager_name || 'manager'}
                                                    </p>
                                                </div>
                                                <ChevronRight className="w-4 h-4 text-slate-600 shrink-0" />
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </TabsContent>
                    </Tabs>

                    {couponTab === 'new' && (
                        <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
                            <Button
                                variant="outline"
                                onClick={saveAsTemplate}
                                disabled={savingTemplate || savingCoupon || !couponReason || !((couponShowCustomValue ? parseFloat(couponCustomValue) > 0 : couponValue > 0))}
                                className="w-full sm:w-auto min-h-[44px]"
                                data-testid="coupon-save-template-btn"
                            >
                                {savingTemplate
                                    ? <Loader2 className="w-4 h-4 animate-spin mr-1" />
                                    : <BookmarkPlus className="w-4 h-4 mr-1" />}
                                {editingTemplateId ? t('manager.updateTemplate') : t('manager.saveAsTemplate')}
                            </Button>
                            <Button
                                onClick={submitCoupon}
                                disabled={savingCoupon || !couponReason || !(couponShowCustomValue ? parseFloat(couponCustomValue) > 0 : couponValue > 0)}
                                className="w-full sm:w-auto min-h-[44px] bg-emerald-600 hover:bg-emerald-700"
                                data-testid="coupon-submit-btn"
                            >
                                {savingCoupon
                                    ? <Loader2 className="w-4 h-4 animate-spin mr-1" />
                                    : <Check className="w-4 h-4 mr-1" />}
                                {t('manager.couponIssueBtn', { value: (couponShowCustomValue ? (parseFloat(couponCustomValue) || 0) : (couponValue || 0)) || '—' })}
                            </Button>
                        </DialogFooter>
                    )}
                </DialogContent>
            </Dialog>

            {/* Delete template confirmation */}
            <Dialog open={!!confirmDeleteTemplate} onOpenChange={(o) => { if (!o) setConfirmDeleteTemplate(null); }}>
                <DialogContent data-testid="coupon-template-delete-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Trash2 className="w-5 h-5 text-red-500" /> {t('manager.templateDeleteConfirm')}
                        </DialogTitle>
                        <DialogDescription>{confirmDeleteTemplate?.name}</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmDeleteTemplate(null)}>{t('manager.cancel')}</Button>
                        <Button variant="destructive" onClick={() => deleteTemplate(confirmDeleteTemplate.id)} data-testid="coupon-template-delete-confirm">
                            <Trash2 className="w-4 h-4 mr-1" /> {t('manager.templateDeleteBtn')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Manage Access Modal */}
            <Dialog open={accessOpen} onOpenChange={setAccessOpen}>
                <DialogContent className="max-w-[calc(100vw-1rem)] sm:max-w-3xl max-h-[92vh] overflow-y-auto p-4 sm:p-6" data-testid="mgr-access-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><UserCog className="w-5 h-5 shrink-0" /> <span className="truncate">{t('manager.manageAccess')}</span></DialogTitle>
                        <DialogDescription className="truncate">{t('manager.registry')}</DialogDescription>
                    </DialogHeader>
                    <ManagerAccessRegistry
                        onAction={bumpActivity}
                        scope={{ organization_id: scope.organization_id, is_super_admin: scope.role === 'super_admin' }}
                        sessionToken={token}
                    />
                </DialogContent>
            </Dialog>
            <Toaster position="top-center" data-testid="mgr-session-toaster" />
        </div>
    );
}

function Section({ title, icon, children }) {
    return (
        <Card className="bg-slate-900/40 border-slate-800 yp-elevate">
            <CardContent className="pt-4 space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-200">
                    {icon}<span>{title}</span>
                </div>
                {children}
            </CardContent>
        </Card>
    );
}

function MetricCard({ label, value, color = 'slate', testid }) {
    const colorMap = {
        slate: 'text-white',
        emerald: 'text-emerald-400',
        blue: 'text-blue-400',
        amber: 'text-amber-400',
    };
    return (
        <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-2.5 yp-elevate h-full flex flex-col justify-between min-h-[76px] overflow-hidden" data-testid={testid}>
            <p className="text-[10px] uppercase text-slate-500 leading-tight min-h-[26px] break-words">{label}</p>
            <p className={`text-2xl font-bold leading-none ${colorMap[color]}`}>{value}</p>
        </div>
    );
}

function PinGate({ cardId, onSuccess, onBack }) {
    const { t } = useTranslation();
    const [pin, setPin] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);

    const submit = async () => {
        if (pin.length !== 4 && pin.length !== 6) {
            setErr(t('manager.pinHint4or6'));
            return;
        }
        setSubmitting(true);
        setErr('');
        try {
            const res = await fetch(`${API_BASE}/api/manager/verify-pin`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ card_id: cardId, pin }),
            });
            if (res.ok) {
                const data = await res.json();
                setShowSuccess(true);
                // Show the success checkmark for ~400ms before mounting the dashboard
                setTimeout(() => onSuccess(data), 420);
            } else {
                const e = await res.json().catch(() => ({}));
                setErr(e.detail || t('manager.invalidPin'));
                setPin('');
            }
        } catch { setErr(t('manager.invalidPin')); }
        setSubmitting(false);
    };

    return (
        <div
            className="min-h-screen text-white flex flex-col items-center"
            data-testid="manager-pin-gate"
            style={{
                backgroundImage: `linear-gradient(180deg, rgba(2,6,23,0) 0%, rgba(2,6,23,0.2) 42%, rgba(2,6,23,0.85) 72%, rgba(2,6,23,0.98) 100%), url(${neoLandingBg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center top',
                backgroundRepeat: 'no-repeat',
                backgroundColor: '#020617',
            }}
        >
            {/* Top bar — Back button (functional, always-bilingual) */}
            <div className="w-full max-w-sm px-4 pt-4">
                <button
                    onClick={onBack}
                    className="text-xs text-slate-300 hover:text-white flex items-center gap-1 bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/10 transition-colors"
                    data-testid="mgr-pin-back"
                >
                    <X className="w-3 h-3" /> {t('common.back', 'Back')}
                </button>
            </div>

            {/* Spacer to let the NEO logo in the background image breathe */}
            <div className="flex-1 min-h-[44vh]" aria-hidden="true" />

            {/* Functional auth card */}
            <div className="w-full max-w-sm px-5 pb-4" data-testid="mgr-pin-card">
                {showSuccess ? (
                    <div className="flex flex-col items-center gap-4 py-6 bg-black/40 backdrop-blur-md rounded-3xl border border-white/10" data-testid="mgr-pin-success">
                        <SuccessCheck size={72} colorClass="text-emerald-400" />
                        <p className="text-emerald-300 font-semibold text-lg tracking-[0.3em]">NEO</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* Real PIN input — the image itself already prints "Enter Manager PIN"
                            above this field; no duplicate overlay label. aria-label keeps the
                            field accessible to screen readers. */}
                        <div className="relative">
                            <Input
                                type="password"
                                inputMode="numeric"
                                autoFocus
                                maxLength={6}
                                value={pin}
                                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                onKeyDown={(e) => e.key === 'Enter' && submit()}
                                className="text-center text-3xl tracking-[0.6em] bg-black/50 backdrop-blur-md border border-white/20 h-14 rounded-2xl shadow-inner text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-white/40"
                                placeholder="••••"
                                data-testid="mgr-pin-input"
                                aria-label={t('manager.enterManagerPin', 'Enter Manager PIN')}
                            />
                        </div>
                        {err && (
                            <p
                                className="text-center text-sm text-red-300 bg-red-950/60 border border-red-500/30 backdrop-blur-sm rounded-xl py-2 animate-in fade-in duration-200"
                                data-testid="mgr-pin-error"
                            >
                                {err}
                            </p>
                        )}
                        <Button
                            onClick={submit}
                            disabled={submitting || (pin.length !== 4 && pin.length !== 6)}
                            className="w-full h-12 text-base font-semibold rounded-2xl bg-white hover:bg-white text-slate-900 border border-white/50 shadow-xl backdrop-blur-sm disabled:bg-white/85 disabled:text-slate-700 disabled:border-white/40 disabled:cursor-not-allowed"
                            data-testid="mgr-pin-submit"
                        >
                            {submitting
                                ? <PulseIcon icon={Lock} size={16} colorClass="text-slate-900" className="mr-1" />
                                : <KeyRound className="w-4 h-4 mr-1.5" />}
                            {t('common.login', 'Login')}
                        </Button>
                    </div>
                )}
            </div>

            {/* Powered by GlobalOnTap — bilingual, sits over the image's printed signature */}
            <div className="w-full text-center pt-3 pb-5">
                <p
                    className="text-[11px] uppercase tracking-[0.35em] text-white/70 drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)]"
                    data-testid="mgr-pin-powered-by"
                >
                    {t('landing.neoPoweredBy', 'Powered by GlobalOnTap')}
                </p>
            </div>
        </div>
    );
}
